import { autoinject } from 'aurelia-dependency-injection';
import { CategoriesSettingsViewModel, CustomerSettingsClient, CustomerViewModel } from '../clients/transaction-clients';
import { CustomersClient } from '../clients/policy-clients';
import { ReplaySubject } from 'rxjs/internal/ReplaySubject';
import { IApplicationFilter } from '../interfaces/i-application-filter';
import { IDateInterval } from '../interfaces/i-date-interval';
import { predefinedDateIntervals } from './get-predefined-date-intervals';
import { chartThemes } from './chart-themes';
import { format } from 'date-fns/format';
import { saveAs } from 'file-saver';
import * as ExcelJS from 'exceljs';
import { IExcelExportData } from '../interfaces/i-excel-export-data';
import { MdcMenu } from '@aurelia-mdc-web/menu';

@autoinject
export class CustomerService {
  constructor(private customersClient: CustomersClient, private customerSettingsClient: CustomerSettingsClient) {
    this.selectDates(predefinedDateIntervals[3]);
  }

  private data: Promise<CustomerViewModel[]>;
  selectedCustomer: CustomerViewModel;
  previousSelectedCustomer: CustomerViewModel;
  selectedDates: IDateInterval;
  selectedCustomer$ = new ReplaySubject<CustomerViewModel>(1);
  customerAndDates$ = new ReplaySubject<IApplicationFilter>(1);
  systemInstrumentCategories: CategoriesSettingsViewModel[];
  userInstrumentCategories: CategoriesSettingsViewModel[];
  instrumentCategories: CategoriesSettingsViewModel[];
  transactionCategories: CategoriesSettingsViewModel[];

  excelData: IExcelExportData;
  excelExportMenuAnchor: HTMLDivElement;
  exportMenu: MdcMenu;

  async get(refresh = false) {
    if (!this.data || refresh) {
      this.data = this.customersClient.myCustomers() as unknown as Promise<CustomerViewModel[]>;
    }

    return this.data;
  }

  selectCustomer(customer: CustomerViewModel) {
    this.previousSelectedCustomer = this.selectedCustomer;
    this.selectedCustomer = customer;
    this.selectedCustomer$.next(customer);
    this.publishFilter();
  }

  selectDates(i: IDateInterval) {
    i.fromStr = format(i.from, 'yyyy-MM-dd');
    i.toStr = format(i.to, 'yyyy-MM-dd');
    this.selectedDates = i;
    this.publishFilter();
  }

  private publishFilter() {
    this.customerAndDates$.next({
      customer: this.selectedCustomer,
      dates: this.selectedDates
    });
  }

  getChartTheme(name: string | undefined) {
    const themeName = name ?? this.selectedCustomer.defaultChartTheme;
    return themeName ? chartThemes.find(x => x.name === themeName).theme : chartThemes[0].theme;
  }

  async initCategories() {
    const settings = await this.customerSettingsClient.get(this.selectedCustomer.companyId);
    this.instrumentCategories = settings.instrumentCategories;
    this.userInstrumentCategories = settings.instrumentCategories.filter(x => x.isSystem === false && x.enabled);
    this.systemInstrumentCategories = settings.instrumentCategories.filter(x => x.isSystem === true && x.enabled);
    this.transactionCategories = settings.transactionCategories;
  }

  async exportWidget(): Promise<void> {
    try {
      console.log('Starting export...', this.excelData);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(this.excelData.fileName);

      // Get items array
      const items = this.excelData.data?.Items;
      if (!items || items.length === 0) {
        throw new Error('No data to export');
      }

      console.log('Exporting', items.length, 'items');

      // Get column headers from first item
      const headers = Object.keys(items[0]);
      console.log('Headers:', headers);

      // Add header row
      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFD9E1F2' }
      };

      // Add data rows
      items.forEach(item => {
        const row = worksheet.addRow(headers.map(header => item[header] ?? ''));

        // Format numbers
        headers.forEach((header, index) => {
          const value = item[header];
          if (typeof value === 'number' && !Number.isInteger(value)) {
            row.getCell(index + 1).numFmt = '#,##0.00';
          }
        });
      });

      // Auto-fit columns
      headers.forEach((header, index) => {
        const maxLength = Math.max(
          header.length,
          ...items.map(item => {
            const value = item[header];
            return value ? value.toString().length : 0;
          })
        );
        worksheet.getColumn(index + 1).width = Math.min(maxLength + 2, 50);
      });

      // Save file
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `${this.excelData.fileName}_${timestamp}.xlsx`;

      console.log('Writing buffer...');
      const buffer = await workbook.xlsx.writeBuffer();
      console.log('Buffer created, size:', buffer.byteLength);

      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      console.log('Blob created, size:', blob.size);

      saveAs(blob, filename);
      console.log('File saved:', filename);
    } catch (error) {
      console.error('Export failed:', error);
      throw error;
    }
  }

}
